import { Column } from '@access-erp/common';
import { CellType, ICellRendererParams, Tooltip } from '@access-erp/ui-components';

import { CellFormatters } from '@/components/common/EnquiriesScreen/utils/CellFormatters';
import { NOT_AVAILABLE_CHAR } from '@/config/common.config';
import { ContractLineBillingCycleOutput } from '@/graphql/contracts/_generated_/contracts';
import { dateCellRenderer, lastNextDayGap } from '@/pages/ContractDetails/ContractLines/grid/helper';

const VISIBLE_REASON_CHARS_LENGTH = 50;

export const getDetailColumns = (): Column<any>[] => [
  {
    name: 'contractNumber',
    caption: 'Contract Number',
    width: 200,
    cellType: CellType.String,
    filterable: false,
    suppressAdvancedFilter: true,
  },
  {
    name: 'customerCode',
    caption: 'Contracted Account',
    width: 200,
    cellType: CellType.String,
    filterable: false,
    suppressAdvancedFilter: true,
  },
  {
    name: 'customerName',
    caption: 'Contracted Name',
    width: 200,
    cellType: CellType.String,
    filterable: false,
    suppressAdvancedFilter: true,
  },
  {
    name: 'lineStartDate',
    caption: 'Line Start Date',
    width: 180,
    cellType: CellType.Date,
    cellFormatter: CellFormatters.date,
    filterable: false,
    suppressAdvancedFilter: true,
  },
  {
    name: 'billingInterval',
    caption: 'Billing Interval',
    width: 160,
    cellType: CellType.String,
    filterable: false,
    suppressAdvancedFilter: true,
  },
  {
    name: 'minCharge',
    caption: 'Min Charge',
    width: 160,
    cellType: CellType.String,
    filterable: false,
    suppressAdvancedFilter: true,
    cellStyle: { justifyContent: 'end' },
  },
  {
    name: 'salesHandoverNumber',
    caption: 'Sales Handover Number',
    width: 180,
    cellType: CellType.String,
    filterable: false,
    suppressAdvancedFilter: true,
  },
  {
    name: 'contractStatus',
    caption: 'Contract Status',
    width: 170,
    cellType: CellType.String,
    filterable: false,
    suppressAdvancedFilter: true,
  },
  {
    name: 'stockCode',
    caption: 'Stock Code',
    width: 200,
    cellType: CellType.String,
    filterable: false,
    suppressAdvancedFilter: true,
  },
  {
    name: 'quantity',
    caption: 'Quantity',
    width: 160,
    cellType: CellType.Decimal,
    filterable: false,
    suppressAdvancedFilter: true,
  },
  {
    name: 'unitType',
    caption: 'Unit Type',
    width: 100,
    cellType: CellType.String,
    filterable: false,
    suppressAdvancedFilter: true,
  },
  {
    name: 'unitPrice',
    caption: 'Unit Price',
    width: 170,
    cellType: CellType.String,
    filterable: false,
    suppressAdvancedFilter: true,
    cellStyle: { justifyContent: 'end' },
  },
  {
    name: 'unitPriceFrequency',
    caption: 'Unit Price Frequency',
    width: 150,
    cellType: CellType.String,
    valuePath: 'unitPriceFrequency',
    filterable: false,
    suppressAdvancedFilter: true,
  },
  {
    name: 'billingType',
    caption: 'Billing Type',
    width: 280,
    cellType: CellType.String,
    cellRenderer: (params: ICellRendererParams<any>) => {
      const billingTypesArray = params.data?.billingType?.split?.(',');
      return billingTypesArray?.join(', ') ?? '';
    },
    filterable: false,
    suppressAdvancedFilter: true,
  },
  {
    name: 'nextInvoiceDate',
    caption: 'Next Invoice Date',
    width: 180,
    cellType: CellType.Date,
    cellFormatter: CellFormatters.date,
    groupable: false,
    disableRowGroup: true,
    sortable: false,
    filterable: false,
    suppressAdvancedFilter: true,
  },
  {
    name: 'statusChangeComment',
    caption: 'Comments',
    width: 200,
    cellType: CellType.String,
    filterable: false,
    suppressAdvancedFilter: true,
  },
  {
    name: 'billingPeriodValue',
    caption: 'Billing Period Value',
    cellType: CellType.String,
    width: 180,
    filterable: false,
    suppressAdvancedFilter: true,
    cellStyle: { justifyContent: 'end' },
  },
  {
    name: 'lastSOQuantity',
    caption: 'Last SO Quantity',
    cellType: CellType.Integer,
    width: 180,
    groupable: false,
    disableRowGroup: true,
    sortable: false,
    filterable: false,
    suppressAdvancedFilter: true,
    cellStyle: { justifyContent: 'end' },
  },
  {
    name: 'lastSOTotalValue',
    caption: 'Last SO Total Value',
    cellType: CellType.String,
    width: 180,
    groupable: false,
    disableRowGroup: true,
    sortable: false,
    filterable: false,
    suppressAdvancedFilter: true,
    cellStyle: { justifyContent: 'end' },
  },
  {
    name: 'lastSOStartDate',
    caption: 'Last SO Start Date',
    cellType: CellType.Date,
    cellFormatter: CellFormatters.date,
    minWidth: 180,
    groupable: false,
    disableRowGroup: true,
    sortable: false,
    filterable: false,
    suppressAdvancedFilter: true,
  },
  {
    name: 'lastSOEndDate',
    caption: 'Last SO End Date',
    cellType: CellType.Date,
    cellFormatter: CellFormatters.date,
    minWidth: 180,
    groupable: false,
    disableRowGroup: true,
    sortable: false,
    filterable: false,
    suppressAdvancedFilter: true,
  },
  {
    name: 'annualBillingPeriodValue',
    caption: 'Annual Billing Period Value',
    cellType: CellType.String,
    width: 180,
    filterable: false,
    suppressAdvancedFilter: true,
    cellStyle: { justifyContent: 'end' },
  },
  {
    name: 'predictedSOStartDate',
    caption: 'Predicted SO start date',
    width: 180,
    cellType: CellType.Date,
    cellFormatter: CellFormatters.date,
    groupable: false,
    disableRowGroup: true,
    sortable: false,
    filterable: false,
    suppressAdvancedFilter: true,
  },
  {
    name: 'predictedSOEndDate',
    caption: 'Predicted SO end date',
    width: 180,
    cellType: CellType.Date,
    cellFormatter: CellFormatters.date,
    groupable: false,
    disableRowGroup: true,
    sortable: false,
    filterable: false,
    suppressAdvancedFilter: true,
  },
  {
    name: 'predictedSOInvoiceDate',
    caption: 'Predicted SO invoice date',
    width: 180,
    cellType: CellType.Date,
    cellFormatter: CellFormatters.date,
    groupable: false,
    disableRowGroup: true,
    sortable: false,
    filterable: false,
    suppressAdvancedFilter: true,
  },
  {
    name: 'predictedAnnualValue',
    caption: 'Predicted Annual Value',
    width: 180,
    cellType: CellType.String,
    groupable: false,
    disableRowGroup: true,
    sortable: false,
    filterable: false,
    suppressAdvancedFilter: true,
    cellStyle: { justifyContent: 'end' },
  },
  {
    name: 'nextBillingStartDate',
    caption: 'Next Billing Start Date',
    width: 180,
    cellType: CellType.Date,
    cellFormatter: CellFormatters.date,
    cellRenderer: ({
      value,
      data,
    }: ICellRendererParams<ContractLineBillingCycleOutput, string>) => {
    
      let displayValue = '';
    
      if (!value && data?.nextBillingStartDate == null) {
        displayValue = NOT_AVAILABLE_CHAR;
      }
    
      let dayGap = lastNextDayGap(data);
      displayValue = dateCellRenderer({ value });
    
      return (<>
            {(dayGap > 1) && 
            <Tooltip tooltipFor={<span>{'\u26A0\uFE0F'}</span>}>
              There is a {dayGap}-day gap between the last and next billed periods.
            </Tooltip>}
            {displayValue}
            </>);
    },
    groupable: false,
    disableRowGroup: true,
    sortable: false,
    filterable: false,
    suppressAdvancedFilter: true,
  },
  {
    name: 'nextBillingEndDate',
    caption: 'Next Billing End Date',
    width: 180,
    cellType: CellType.Date,
    cellFormatter: CellFormatters.date,
    cellRenderer: ({
      value,
      data,
    }: ICellRendererParams<ContractLineBillingCycleOutput, string>) => {
    
      let displayValue = '';
    
      if (!value && data?.nextBillingEndDate == null) {
        return NOT_AVAILABLE_CHAR;
      }
    
      let dayGap = lastNextDayGap(data);
      displayValue = dateCellRenderer({ value });
    
      return (<>
            {(dayGap > 1) && 
            <Tooltip tooltipFor={<span>{'\u26A0\uFE0F'}</span>}>
              There is a {dayGap}-day gap between the last and next billed periods.
            </Tooltip>}
            {displayValue}
            </>);
    },
    groupable: false,
    disableRowGroup: true,
    sortable: false,
    filterable: false,
    suppressAdvancedFilter: true,
  },
  {
    name: 'lastBillingStartDate',
    caption: 'Last Billing Start Date',
    width: 180,
    cellType: CellType.Date,
    cellFormatter: CellFormatters.date,
    groupable: false,
    disableRowGroup: true,
    sortable: false,
    filterable: false,
    suppressAdvancedFilter: true,
  },
  {
    name: 'lastBillingEndDate',
    caption: 'Last Billing End Date',
    width: 180,
    cellType: CellType.Date,
    cellFormatter: CellFormatters.date,
    groupable: false,
    disableRowGroup: true,
    sortable: false,
    filterable: false,
    suppressAdvancedFilter: true,
  },
];

export const prodHiddenDetailColumns: Column<any>[] = [
  {
    name: 'shipToCustomerAccountNumber',
    caption: 'Ship to Account',
    width: 200,
    cellType: CellType.String,
    filterable: false,
    suppressAdvancedFilter: true,
  },
  {
    name: 'shipToCustomerName',
    caption: 'Ship to Name',
    width: 200,
    cellType: CellType.String,
    filterable: false,
    suppressAdvancedFilter: true,
  },
  {
    name: 'shipToCustomerAddress',
    caption: 'Ship to Address',
    width: 200,
    cellType: CellType.String,
    filterable: false,
    suppressAdvancedFilter: true,
  },
  {
    name: 'shipToCustomerPostcode',
    caption: 'Ship to Postcode',
    width: 200,
    cellType: CellType.String,
    filterable: false,
    suppressAdvancedFilter: true,
  },
  {
    name: 'invoiceToCustomerAccountNumber',
    caption: 'Invoice to Account',
    width: 200,
    cellType: CellType.String,
    filterable: false,
    suppressAdvancedFilter: true,
  },
  {
    name: 'invoiceToCustomerName',
    caption: 'Invoice to Name',
    width: 200,
    cellType: CellType.String,
    filterable: false,
    suppressAdvancedFilter: true,
  },
  {
    name: 'invoiceToCustomerAddress',
    caption: 'Invoice to Address',
    width: 200,
    cellType: CellType.String,
    filterable: false,
    suppressAdvancedFilter: true,
  },
  {
    name: 'invoiceToCustomerPostcode',
    caption: 'Invoice to Postcode',
    width: 200,
    cellType: CellType.String,
    filterable: false,
    suppressAdvancedFilter: true,
  },
  {
    name: 'poNumber',
    caption: 'PO Number',
    width: 160,
    cellType: CellType.String,
    filterable: false,
    suppressAdvancedFilter: true,
  },
  {
    name: 'poTotalValue',
    caption: 'PO Total Value',
    width: 200,
    cellType: CellType.String,
    groupable: false,
    filterable: false,
    suppressAdvancedFilter: true,
    cellStyle: { justifyContent: 'end' },
  },
  {
    name: 'poTotalValue',
    caption: 'PO Remaining Value',
    width: 200,
    cellType: CellType.String,
    groupable: false,
    filterable: false,
    suppressAdvancedFilter: true,
    cellStyle: { justifyContent: 'end' },
  },
  {
    name: 'poDate',
    caption: 'PO Date',
    width: 160,
    cellType: CellType.Date,
    cellFormatter: CellFormatters.date,
    filterable: false,
    suppressAdvancedFilter: true,
  },
  {
    name: 'poStartDate',
    caption: 'PO Start Date',
    width: 200,
    cellType: CellType.Date,
    cellFormatter: CellFormatters.date,
    filterable: false,
    suppressAdvancedFilter: true,
  },
  {
    name: 'poEndDate',
    caption: 'PO End Date',
    width: 200,
    cellType: CellType.Date,
    cellFormatter: CellFormatters.date,
    filterable: false,
    suppressAdvancedFilter: true,
  },
  {
    name: 'poStatus',
    caption: 'PO Status',
    width: 160,
    cellType: CellType.String,
    cellRenderer: ({ value }: any) =>
      !!value && value !== 'Active' ? <span>{'\u26A0\uFE0F ' + value}</span> : <span>{value}</span>,
    filterable: false,
    suppressAdvancedFilter: true,
  },
  {
    name: 'poNote',
    caption: 'PO Note',
    width: 250,
    cellType: CellType.String,
    filterable: false,
    suppressAdvancedFilter: true,
    disableRowGroup: true,
    sortable: false,
    cellRenderer: ({ value }: any) => {
      const reasonForFailureTrimmed =
        value && value.length > VISIBLE_REASON_CHARS_LENGTH
          ? value.slice(0, VISIBLE_REASON_CHARS_LENGTH) + '...'
          : value || '';

      const titlePopUp = value && value.length > VISIBLE_REASON_CHARS_LENGTH ? value : undefined;

      return <div title={titlePopUp}>{reasonForFailureTrimmed}</div>;
    },
  },
  {
    name: 'oppHeaderId',
    caption: 'Opp Header Id',
    width: 180,
    cellType: CellType.String,
    defaultFilter: 49,
  },
  {
    name: 'oppLineId',
    caption: 'Opp Line Id',
    width: 180,
    cellType: CellType.String,
    defaultFilter: 50,
  },
];
